import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent,
  Button, CircularProgress, Chip
} from '@mui/material';
import {
  Favorite, LocalFireDepartment, MusicNote,
  Book, People, Person, Logout, Air, Phone,KeyboardVoice
} from '@mui/icons-material';
import {
  getMyProfile, getMoodSummary,
  getTodayDevotional, getMyStreaks
} from '../services/api';
import NotificationBell from '../components/NotificationBell';

// Time-based greeting
function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return { text: 'Good morning', emoji: '☀️' };
  if (hour < 17) return { text: 'Good afternoon', emoji: '🌤️' };
  return { text: 'Good evening', emoji: '🌙' };
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [moodSummary, setMoodSummary] = useState(null);
  const [devotional, setDevotional] = useState(null);
  const [streaks, setStreaks] = useState([]);
  const [loading, setLoading] = useState(true);
  const greeting = getGreeting();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) navigate('/');
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [profileRes, devotionalRes, streaksRes] = await Promise.all([
        getMyProfile(),
        getTodayDevotional(),
        getMyStreaks(),
      ]);
      setProfile(profileRes.data);
      setDevotional(devotionalRes.data);
      setStreaks(streaksRes.data);
      try {
        const moodRes = await getMoodSummary();
        setMoodSummary(moodRes.data);
      } catch (e) {}
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/');
      }
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#0a1628' }}>
      <CircularProgress sx={{ color: '#4ade80' }} />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(160deg, #0a1628 0%, #0d1f3c 60%, #0a2040 100%)', pb: 6 }}>

      {/* Kenyan flag accent strip */}
      <Box sx={{ display: 'flex', height: 8 }}>
        <Box sx={{ flex: 1, background: '#000000' }} />
        <Box sx={{ flex: 1, background: '#bb0000' }} />
        <Box sx={{ flex: 1, background: '#006600' }} />
      </Box>

      {/* Header */}
      <Box sx={{
        background: 'rgba(255,255,255,0.02)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        px: 3, py: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{
            color: '#4ade80',
            fontWeight: 900,
            fontSize: '1.6rem',
            letterSpacing: '-0.5px',
            lineHeight: 1,
          }}>
            Imara
          </Typography>
          <Box sx={{
            background: 'rgba(74,222,128,0.12)',
            border: '1px solid rgba(74,222,128,0.25)',
            borderRadius: 1,
            px: 0.8, py: 0.2,
            ml: 0.5,
          }}>
            <Typography sx={{ color: '#4ade80', fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.08em' }}>
              🇰🇪 KE
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <NotificationBell streaks={streaks} moodSummary={moodSummary} />
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>
            {profile?.username}
          </Typography>
          <Button onClick={handleLogout} sx={{ color: 'rgba(255,255,255,0.25)', minWidth: 0, p: 0.5 }}>
            <Logout fontSize="small" />
          </Button>
        </Box>
      </Box>

      <Box sx={{ px: 3, pt: 3 }}>

        {/* Greeting */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem', mb: 0.3 }}>
            {new Date().toLocaleDateString('en-KE', { weekday: 'long', month: 'long', day: 'numeric' })}
          </Typography>
          <Typography variant="h5" sx={{ color: 'white', fontWeight: 800, fontSize: '1.4rem', lineHeight: 1.2 }}>
            {greeting.text}, {profile?.username} {greeting.emoji}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem', mt: 0.5 }}>
            Imara — Strong & Firm 🌿
          </Typography>
        </Box>

        {/* Stat cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            {
              value: profile?.mood_count || 0,
              label: 'Moods Logged',
              color: '#4ade80',
              border: 'rgba(74,222,128,0.3)',
              glow: 'rgba(74,222,128,0.08)',
              icon: '😊',
            },
            {
              value: streaks.length || 0,
              label: 'Active Streaks',
              color: '#f97316',
              border: 'rgba(249,115,22,0.3)',
              glow: 'rgba(249,115,22,0.08)',
              icon: '🔥',
            },
            {
              value: moodSummary ? moodSummary.average_score.toFixed(1) : '-',
              label: 'Avg Mood',
              color: '#22d3ee',
              border: 'rgba(34,211,238,0.3)',
              glow: 'rgba(34,211,238,0.08)',
              icon: '📊',
            },
          ].map((stat) => (
            <Grid item xs={4} key={stat.label}>
              <Card sx={{
                background: stat.glow,
                border: `1px solid ${stat.border}`,
                borderRadius: 3,
                boxShadow: `0 0 16px ${stat.glow}`,
              }}>
                <CardContent sx={{ textAlign: 'center', py: 2, px: 1 }}>
                  <Typography sx={{ fontSize: '1.1rem', mb: 0.3 }}>{stat.icon}</Typography>
                  <Typography sx={{ color: stat.color, fontWeight: 900, fontSize: '1.7rem', lineHeight: 1 }}>
                    {stat.value}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.62rem', mt: 0.5 }}>
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Devotional */}
        {devotional && (
          <Card sx={{
            background: 'rgba(74,222,128,0.04)',
            border: '1px solid rgba(74,222,128,0.18)',
            borderLeft: '3px solid #4ade80',
            borderRadius: 3,
            mb: 2,
          }}>
            <CardContent>
              <Typography sx={{ color: '#4ade80', fontWeight: 700, mb: 1, fontSize: '0.72rem', letterSpacing: '0.12em' }}>
                ✨ TODAY'S DEVOTIONAL
              </Typography>
              <Typography sx={{ color: 'white', fontWeight: 700, mb: 1, fontSize: '1rem' }}>
                {devotional.scripture_reference}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.83rem', mb: 2, fontStyle: 'italic', lineHeight: 1.6 }}>
                "{devotional.scripture_text}"
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', mb: 2, lineHeight: 1.6 }}>
                {devotional.reflection}
              </Typography>
              <Chip
                label={devotional.playlist_suggestion}
                sx={{ background: 'rgba(74,222,128,0.1)', color: '#4ade80', fontSize: '0.68rem', border: '1px solid rgba(74,222,128,0.2)' }}
              />
            </CardContent>
          </Card>
        )}

        {/* Active Streaks */}
        {streaks.length > 0 && (
          <Card sx={{
            background: 'rgba(249,115,22,0.04)',
            border: '1px solid rgba(249,115,22,0.18)',
            borderLeft: '3px solid #f97316',
            borderRadius: 3,
            mb: 2,
          }}>
            <CardContent>
              <Typography sx={{ color: '#f97316', fontWeight: 700, mb: 2, fontSize: '0.72rem', letterSpacing: '0.12em' }}>
                🔥 ACTIVE STREAKS
              </Typography>
              {streaks.map(streak => (
                <Box key={streak.id} sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1, p: 1.5,
                  background: 'rgba(249,115,22,0.06)',
                  borderRadius: 2,
                }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.8)', textTransform: 'capitalize', fontSize: '0.88rem' }}>
                    {streak.streak_type}
                  </Typography>
                  <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: '0.88rem' }}>
                    {streak.current_count} days 🔥
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Quick Access */}
        <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.72rem', letterSpacing: '0.12em', mb: 2, mt: 1 }}>
          QUICK ACCESS
        </Typography>
        <Grid container spacing={1.5}>
          {[
            { label: 'Mood', icon: <Favorite sx={{ fontSize: 22 }} />, color: '#ec4899', bg: 'rgba(236,72,153,0.1)', path: '/mood' },
            { label: 'Streaks', icon: <LocalFireDepartment sx={{ fontSize: 22 }} />, color: '#f97316', bg: 'rgba(249,115,22,0.1)', path: '/streaks' },
            { label: 'Music', icon: <MusicNote sx={{ fontSize: 22 }} />, color: '#a855f7', bg: 'rgba(168,85,247,0.1)', path: '/music' },
            { label: 'Community', icon: <People sx={{ fontSize: 22 }} />, color: '#22d3ee', bg: 'rgba(34,211,238,0.1)', path: '/community' },
            { label: 'Devotional', icon: <Book sx={{ fontSize: 22 }} />, color: '#4ade80', bg: 'rgba(74,222,128,0.1)', path: '/devotional' },
            { label: 'Profile', icon: <Person sx={{ fontSize: 22 }} />, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', path: '/profile' },
            { label: 'Breathe', icon: <Air sx={{ fontSize: 22 }} />, color: '#22d3ee', bg: 'rgba(34,211,238,0.1)', path: '/breathing' },
            { label: 'Crisis', icon: <Phone sx={{ fontSize: 22 }} />, color: '#ef4444', bg: 'rgba(239,68,68,0.1)', path: '/crisis' },
            { label: 'Journal', icon: <KeyboardVoice sx={{ fontSize: 22 }} />, color: '#a855f7', bg: 'rgba(168,85,247,0.1)', path: '/voice' },
          ].map((item) => (
            <Grid item xs={3} key={item.label}>
              <Card
                onClick={() => navigate(item.path)}
                sx={{
                  background: item.bg,
                  border: `1px solid ${item.color}33`,
                  borderRadius: 3,
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.18s ease',
                  '&:hover': {
                    borderColor: item.color,
                    transform: 'translateY(-3px)',
                    boxShadow: `0 4px 16px ${item.color}22`,
                  },
                  '&:active': { transform: 'scale(0.96)' },
                }}
              >
                <CardContent sx={{ py: 1.8, px: 0.5 }}>
                  <Box sx={{ color: item.color, mb: 0.5 }}>{item.icon}</Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.65rem', fontWeight: 600 }}>
                    {item.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.12)', fontSize: '0.68rem', letterSpacing: '0.05em' }}>
            Built in Nairobi, Kenya 🇰🇪 — for the common mwananchi
          </Typography>
        </Box>

      </Box>
    </Box>
  );
}